import { formatPayDisplay } from "./pay-display";

export interface PayCardDisplay {
  amount: string;
  suffix: string;
  label: string;
}

/**
 * Single-figure pay display shared by the public /opportunities pages and the
 * Ashby job-board description. Shows the top of the range (payRateMax, falling
 * back to payRateMin), rendering a PER_SESSION rate as its hourly equivalent
 * when the guide duration is known. Delegates to formatPayDisplay so applicant
 * emails (which opt out of that conversion) stay in sync with one formatter.
 * Returns null when there's no usable rate so callers can omit the pay element.
 */
export function formatPayCardDisplay(input: {
  payRateMin: string | null;
  payRateMax: string | null;
  payRateFrequency: string;
  estimatedDurationMins?: number | null;
  category?: string | null;
}): PayCardDisplay | null {
  return formatPayDisplay({
    amount: input.payRateMax ?? input.payRateMin,
    payRateFrequency: input.payRateFrequency,
    category: input.category,
    estimatedDurationMins: input.estimatedDurationMins,
    convertSessionToHourly: true,
  });
}

export interface PayStructured {
  amount: number;
  frequency: "HOURLY" | "ONE_TIME" | "PER_TASK";
  display: string;
}

/**
 * Machine-readable form of formatPayCardDisplay for API consumers (partner
 * feeds). Frequency speaks the study pay-cadence vocabulary (the domain truth;
 * callers pass Study.payRateFrequency, not the deprecated listing values).
 * amount and frequency are read off the same displayed figure, so the
 * structured fields can never disagree with `display`. Null when there is no
 * rate.
 */
export function formatPayStructured(
  input: Parameters<typeof formatPayCardDisplay>[0],
): PayStructured | null {
  const display = formatPayCardDisplay(input);
  if (!display) {
    return null;
  }
  const amount = Number(display.amount.replace(/[^0-9.]/g, ""));
  const frequency =
    display.suffix === "/ hr"
      ? "HOURLY"
      : display.suffix === "/ task"
        ? "PER_TASK"
        : "ONE_TIME";
  return {
    amount,
    frequency,
    display: `${display.amount} ${display.suffix}`.trim(),
  };
}

/** The pay fields every advertised figure on a study is formatted from. */
export type AdvertisedPayInput = Parameters<typeof formatPayCardDisplay>[0];

/**
 * The template row's figure where the study resolved one, the listing's own
 * pay columns where it did not.
 *
 * Two sources, never three. `found: false` from the rate read means no
 * template row covers the instant, and the fallback is the listing columns --
 * not `basePaymentAmount`, which is the resolver's synthesised `studyDefault`
 * leg and a different number again. The columns are no longer maintained, so
 * they are the answer only for studies that predate the rate table, and they
 * die with those studies.
 *
 * Returns the input rather than a formatted figure because a card prints more
 * than the number. The cadence badge beside it reads these same fields, and a
 * badge taken from the listing next to a figure taken from the row is how a
 * card comes to say "One-time" above "$40 / hr". Both columns carry the row's
 * single figure, which is also how the generator writes a listing it priced
 * itself: a rate row is one decision, never a range.
 *
 * `rate` is structural rather than the service's type: this module is shared
 * with the browser, and the caller that resolved the rate already knows where
 * it came from.
 */
export function advertisedPayInput(input: {
  rate: { rateUsd: string; payRateFrequency: string } | null | undefined;
  listing: AdvertisedPayInput;
}): AdvertisedPayInput {
  if (!input.rate) {
    return input.listing;
  }
  return {
    ...input.listing,
    payRateMin: input.rate.rateUsd,
    payRateMax: input.rate.rateUsd,
    payRateFrequency: input.rate.payRateFrequency,
  };
}

/** The resolved figure or none, never the listing columns nothing keeps current. */
export function resolvedPayInput(input: {
  rate: { rateUsd: string; payRateFrequency: string } | null | undefined;
  listing: Omit<AdvertisedPayInput, "payRateMin" | "payRateMax">;
}): AdvertisedPayInput {
  return advertisedPayInput({
    rate: input.rate,
    listing: { ...input.listing, payRateMin: null, payRateMax: null },
  });
}

/** What a joiner is paid, never the viewer's own row; referral estimates read it. */
export function joinerFigure<T>(
  rate: (T & { joinerRate?: T }) | null | undefined,
): T | null {
  return rate ? (rate.joinerRate ?? rate) : null;
}
